<script setup lang="ts">
import { cloudLevel, CLOUD_COVER_NO_DATA } from '~/utils/eclipse'
import { cloudToStatus, statusColor } from '~/utils/v0'
import { ECLIPSE_DATE_UTC } from '~/utils/solar'

const { t } = useI18n()

const props = defineProps<{
  forecasts: Array<{
    valid_time: string
    cloud_cover: number | null
    precip_prob?: number | null
  }>
}>()

// Window around totality (17:43-17:48 UTC), date-gated to Aug 12 so a
// 17:30 slot on any other day isn't mis-marked as eclipse-related.
const ECLIPSE_START_UTC = 17 * 60 + 30 // 17:30
const ECLIPSE_END_UTC = 18 * 60 // 18:00

// UTC, not the viewer's local zone. Iceland is UTC+0 year-round and every
// other time on the page (contact times, eclipse-day slot, the hour ticks
// below) is UTC — a local-time tooltip read 2 h off the tick label it sat
// on for anyone outside Iceland.
function formatHour(isoString: string): string {
  const d = new Date(isoString)
  if (isNaN(d.getTime())) return '—'
  return d.toISOString().slice(11, 16)
}

function isEclipseWindow(isoString: string): boolean {
  const d = new Date(isoString)
  const dateUtc = d.toISOString().slice(0, 10) // YYYY-MM-DD
  if (dateUtc !== ECLIPSE_DATE_UTC) return false
  const mins = d.getUTCHours() * 60 + d.getUTCMinutes()
  return mins >= ECLIPSE_START_UTC && mins <= ECLIPSE_END_UTC
}

// Theme-aware 3-band palette via the v0 semantic tokens — matches the
// 10-yr cloud histogram next to it (CloudHistogram.vue) so both charts
// read as the same metric. `cloudToStatus` shares the 40 / 70 % thresholds
// with the rest of the v0 redesign, so the bar colour, the legend chip,
// and the "best region" verdict on /dashboard all agree.
function timelineColor(cover: number | null): string {
  if (cover == null) return CLOUD_COVER_NO_DATA.color
  return statusColor(cloudToStatus(cover))
}

// Reuse labels from shared cloud cover levels (5-band narrative — kept on
// the tooltip so hover detail is still granular even though the bar
// itself collapses to 3 colour bands).
function cloudLabel(cover: number | null): string {
  return t(cloudLevel(cover).labelKey)
}

// Hour-tick labels every 6h (00 / 06 / 12 / 18 UTC) plus the day
// boundary so the timeline reads as a clock, not just two endpoints.
// Skipped if the dataset is too short for ticks to be useful.
interface TickPoint {
  index: number      // index into props.forecasts
  label: string      // "06" / "12" / etc.
  isMidnight: boolean
}
const xAxisTicks = computed<TickPoint[]>(() => {
  if (props.forecasts.length < 8) return []
  const ticks: TickPoint[] = []
  for (let i = 0; i < props.forecasts.length; i++) {
    const fc = props.forecasts[i]!
    const d = new Date(fc.valid_time)
    const h = d.getUTCHours()
    const m = d.getUTCMinutes()
    if (m !== 0) continue
    if (h % 6 !== 0) continue
    ticks.push({
      index: i,
      label: String(h).padStart(2, '0'),
      isMidnight: h === 0,
    })
  }
  return ticks
})
</script>

<template>
  <div v-if="forecasts.length > 0" class="space-y-1">
    <p class="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-3 mb-2">
      {{ t('forecast.timeline_title') }}
    </p>

    <div class="timeline-bars flex gap-0.5 items-end h-12 relative">
      <!-- Bars + their day-boundary gridline overlay -->
      <div
        v-for="(fc, i) in forecasts"
        :key="fc.valid_time"
        class="flex-1 min-w-0 relative group"
        :class="{ 'is-midnight-tick': xAxisTicks.find(t => t.index === i)?.isMidnight }"
        :title="`${formatHour(fc.valid_time)}: ${fc.cloud_cover ?? '?'}% — ${cloudLabel(fc.cloud_cover)}`"
      >
        <div
          class="w-full rounded-t transition-all"
          :class="isEclipseWindow(fc.valid_time) ? 'ring-1 ring-accent/50' : ''"
          :style="{
            height: `${Math.max((fc.cloud_cover ?? 50) / 100 * 40, 4)}px`,
            backgroundColor: timelineColor(fc.cloud_cover),
            opacity: fc.cloud_cover !== null ? 1 : 0.3,
          }"
        />
      </div>
    </div>

    <!-- X-axis: hour ticks every 6h. Falls back to first/last when the
         dataset is shorter than ~8 slots (xAxisTicks empty). -->
    <!-- gap-0.5 must match .timeline-bars exactly: without it the axis
         cells are wider than the bar cells and the tick labels drift
         progressively out of line with the bars they label. -->
    <div v-if="xAxisTicks.length" class="x-axis flex gap-0.5 relative">
      <div
        v-for="(_, i) in forecasts"
        :key="forecasts[i]!.valid_time + '-tick'"
        class="flex-1 min-w-0 text-[9px] font-mono text-center"
      >
        <span
          v-if="xAxisTicks.find(t => t.index === i)"
          :class="xAxisTicks.find(t => t.index === i)!.isMidnight
            ? 'text-ink-3 font-semibold'
            : 'text-ink-3/60 font-normal'"
        >{{ xAxisTicks.find(t => t.index === i)!.label }}</span>
      </div>
    </div>
    <div v-else-if="forecasts.length > 0" class="flex justify-between text-[9px] font-mono text-ink-3/70">
      <span>{{ formatHour(forecasts[0]!.valid_time) }}</span>
      <span
        v-if="forecasts.some(f => isEclipseWindow(f.valid_time))"
        class="text-accent/70 flex items-center gap-0.5"
      >
        <svg class="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="12" r="5" />
          <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" />
        </svg>
        {{ t('forecast.eclipse_window') }}
      </span>
      <span>{{ formatHour(forecasts[forecasts.length - 1]!.valid_time) }}</span>
    </div>

    <!-- Legend — matches the 10-yr histogram's 3-band semantic tokens. -->
    <div class="flex gap-3 text-[9px] font-mono text-ink-3/70 mt-1">
      <span class="flex items-center gap-1">
        <span class="w-1.5 h-1.5 rounded-full bg-good" />Clear
      </span>
      <span class="flex items-center gap-1">
        <span class="w-1.5 h-1.5 rounded-full bg-warn" />Cloudy
      </span>
      <span class="flex items-center gap-1">
        <span class="w-1.5 h-1.5 rounded-full bg-bad" />Overcast
      </span>
    </div>
  </div>

  <div v-else class="text-xs font-mono text-ink-3/70">
    {{ t('forecast.no_data') }}
  </div>
</template>

<style scoped>
/* Vertical day-boundary marker on the bar that sits at midnight UTC.
   Drawn as a left edge so the day handoff reads as a clear gutter,
   not a stripe across the bar's body. */
.timeline-bars .is-midnight-tick::before {
  content: '';
  position: absolute;
  left: -1px;
  top: -2px;
  bottom: -2px;
  width: 1px;
  background: rgb(var(--ink-1) / 0.18);
  pointer-events: none;
}

/* X-axis labels share the bar grid so each tick lines up with its
   bar's center column; just a thin top spacer. */
.x-axis { margin-top: 4px; }
</style>
